import React, { useCallback, useEffect } from "react";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { useLocation, type NavigateFunction } from "react-router";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { routes } from "modules/common/routes";
import { observer } from "mobx-react";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { IndicatorDetails } from "./details/IndicatorsDetails";
import { IndicatorsList } from "./list/IndicatorsList";
import { can } from "modules/session/auth/access";
import functionalities from "modules/dictionaries/functionalities";
import { useMst } from "modules/root/models/hooks";
import styles from "./IndicatorsPage.module.scss";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { Search } from "modules/common/components/form/Search";
import { Link } from "react-router";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

interface IndicatorsPageProps {
  access: string[];
}

const openIndicator = (navigate: NavigateFunction, id: string) => {
  const url = id
    ? routes.dictionaries.orderIndicator.indicatorsDetails(id)
    : routes.dictionaries.orderIndicator.pathIndicators;
  navigate(url);
};

const getSelectedIndicatorId = (pathname: string) => {
  const match = matchRoute<any>(pathname, routes.dictionaries.orderIndicator.indicatorsDetails());
  return match && match.params ? match.params.id : "";
};

export const IndicatorsPage = observer((props: IndicatorsPageProps) => {
  const navigate = useStableNavigate();
  const { pathname } = useLocation();
  const { indicators } = useMst();
  const indicatorId = getSelectedIndicatorId(pathname);

  useEffect(() => {
    document.title = "Ед измерения";
    indicators.list.load();
  }, [indicators.list]);

  // карточка следует за адресом: открыли другую единицу — загружаем её
  useEffect(() => {
    indicators.details.load(indicatorId);
  }, [indicators.details, indicatorId]);

  const onIndicatorSelected = useCallback((id = "") => openIndicator(navigate, id), [navigate]);

  const onIndicatorClicked = (id = "") => {};

  const onIndicatorRemoved = useCallback(() => {
    indicators.list.load();
    navigate(routes.dictionaries.orderIndicator.pathIndicators);
  }, [navigate, indicators.list]);

  const onIndicatorSaved = useCallback(() => {
    openIndicator(navigate, "");
    indicators.list.load();
  }, [navigate, indicators.list]);

  const onIndicatorAdd = useCallback(() => openIndicator(navigate, EMPTY_OBJECT_ID), [navigate]);

  const canChange = can(functionalities.ORDERINDICATOR_EDIT, props.access);

  return (
    <div className={styles.container}>
      <Block columned={true} className={styles.page}>
        <WhenPath path={routes.dictionaries.orderIndicator.indicatorsDetails()}>
          <DetailsPopup title="Ед. измерения" onCancel={onIndicatorSelected}>
            <IndicatorDetails
              indicator={indicators.details}
              onSaved={onIndicatorSaved}
              onRemoved={onIndicatorRemoved}
              canChange={canChange}
            />
          </DetailsPopup>
        </WhenPath>
        <span className={styles.topSpan}>Ед. измерения</span>

        <div className={styles.tools}>
          {canChange && (
            <PlanrButton
              type="secondary"
              size="small"
              icon="general-plus-big"
              onClick={onIndicatorAdd}
              style={{
                // width: "220px",
                marginBottom: "32px",
                borderRadius: "120px",
              }}
            >
              Добавить ед измерения
            </PlanrButton>
          )}
          <Link to={routes.dictionaries.orderIndicator.path} className="planr-button-link">
            <PlanrButton
              type="lightblueish"
              size="small"
              style={{
                width: "200px",
                marginBottom: "32px",
                borderRadius: "120px",
              }}
            >
              ТЭП
            </PlanrButton>
          </Link>
        </div>
        <div className={styles.mobileTools}>
          {canChange && <PlanrButton type="secondary" round icon="general-plus-big" onClick={onIndicatorAdd} />}
        </div>
        <div className={styles.right}>
          <Search query={indicators.list.query} onSearch={indicators.list.setQuery} />
        </div>
        <div className={styles.mobileTools} style={{ marginTop: "10px" }}>
          <Link to={routes.dictionaries.orderIndicator.path} className="planr-button-link">
            <PlanrButton
              type="lightblueish"
              size="small"
              style={{
                width: "200px",
                borderRadius: "120px",
              }}
            >
              ТЭП
            </PlanrButton>
          </Link>
        </div>
        <IndicatorsList
          list={indicators.list}
          onSelected={onIndicatorSelected}
          onClicked={onIndicatorClicked}
          onRemoved={onIndicatorRemoved}
        />
      </Block>
    </div>
  );
});
